import type { CustomerBuyerPersona } from './CustomerBuyerPersona'

export namespace Contacts {
  export const DATA_PLACEHOLDER = '*********'

  export enum ESeniority {
    Junior = 'junior',
    Senior = 'senior',
    Executive = 'executive',
  }

  export enum EType {
    Any = 'any',
    Personal = 'personal',
    Generic = 'generic',
  }

  export enum EDepartment {
    Administration = 'Administration',
    Customer = 'Customer',
    Finance = 'Finance',
    HR = 'HR',
    IT = 'IT',
    Legal = 'Legal',
    Marketing = 'Marketing',
    Operations = 'Operations',
    RD = 'R&D',
    Sales = 'Sales',
    Accounting = 'Accounting',
    Business = 'Business',
    ClientSuccess = 'Client Success',
    Oversight = 'Oversight',
    Production = 'Production',
    Technology = 'Technology',
  }

  export enum EContactQuality {
    HighPlus = 'High Plus',
    High = 'High',
    Medium = 'Medium',
    Low = 'Low',
  }

  export enum ECountryFilterType {
    All = 'all',
    BasedOnLeadCountry = 'based_on_lead_country',
    Selected = 'selected',
  }

  export interface IContact {
    id: string
    type?: EType
    first_name?: string
    last_name?: string
    email?: string
    department?: EDepartment | typeof DATA_PLACEHOLDER | string
    position?: string
    image?: string
    seniority?: ESeniority
    quality?: EContactQuality
    country?: string
    linkedin?: string | typeof DATA_PLACEHOLDER
    twitter?: string
    phone_number?: string | typeof DATA_PLACEHOLDER
    redeemId?: string
  }

  export interface IContactsFilter {
    [key: string]: any
    ids?: string[]
    countries?: string[]
    country_filter_type?: ECountryFilterType
    seniority?: ESeniority[]
    department?: string[]
    keywords?: string[]
    type?: EType
    limit?: number
    with_phone_numbers?: boolean
    with_linkedin_url?: boolean
    with_emails?: boolean
    export_buyer_persona?: boolean
    buyer_persona_id?: number
    buyer_persona_config?: CustomerBuyerPersona['config']
  }

  export interface IContactsResponse {
    contacts: IContact[]
    count: number
    lastReturnedKey?: string
  }

  export interface IBatchContactsOptions {
    [key: string]: IContactsFilter | null
  }

  export interface IBatchContactsResponse {
    [key: string]: IContact[] | null
  }

  export const UNLOCK_SOURCES = [
    'workflow',
    'export',
    'reveal',
    'outreach_connection',
    'auto_engage',
    'albacross_app',
    'chrome_plugin',
    'zapier',
  ] as const
  export type UnlockSource = (typeof UNLOCK_SOURCES)[number]

  // Where an enrichment Auto-Engage bought and reported back (create_log) was started.
  export const AUTO_ENGAGE_SOURCES = [
    'albacross_app',
    'chrome_plugin',
    'auto_engage',
  ] as const satisfies readonly UnlockSource[]
  export type AutoEngageSource = (typeof AUTO_ENGAGE_SOURCES)[number]

  export interface IContactQuota {
    period: string
    clientId: string
    used: number
  }

  export type CreditMeter = 'email' | 'phone'

  export type CreditUsage = { used: number; reserved: number; limit: number }

  export type CreditDenial = { meter: CreditMeter } & CreditUsage

  // The batch answer for a request with a ref: per company, done, wait this many seconds, or no credits.
  export type LedgerContactsResponse = {
    contacts: IBatchContactsResponse
    pending: Record<string, number>
    denied: Record<string, CreditDenial>
  }

  // Where the month was read from: the old logs before the switch instant, the ledger from it.
  export type CreditsSource = 'logs' | 'ledger'

  // phonesCharged: false when the customer's provider delivers phones free with the person.
  export type Credits = { period: string; source: CreditsSource; phonesCharged: boolean } & Record<
    CreditMeter,
    CreditUsage
  >

  // The old usage routes' answer, kept for their callers; period is 'YYYY/M'. charged: the app's phone answer.
  export type UsageAnswer = { period?: string; used: number; total?: number; charged?: boolean }

  export const RESERVATION_STATES = ['held', 'confirmed', 'released', 'expired'] as const
  export type ReservationState = (typeof RESERVATION_STATES)[number]

  // Why a reservation delivered fewer contacts than asked, or none.
  export const RESERVATION_REASONS = [
    'denied',
    'none_found',
    'filtered_out',
    'partial_grant',
    'provider_failed',
    'breaker_open',
    'rate_limited',
    'bad_domain',
  ] as const
  export type ReservationReason = (typeof RESERVATION_REASONS)[number]

  // A confirm after the reservation was released or expired; not a stored reason.
  export type RequestOutcome = ReservationReason | 'late_confirm'
  export const REQUEST_OUTCOMES: RequestOutcome[] = [...RESERVATION_REASONS, 'late_confirm']

  // What support filters requests by; the raw outcomes above stay for engineers.
  export const REQUEST_CATEGORIES = [
    'charged',
    'free',
    'refused',
    'nothing_found',
    'waiting',
    'failed',
    'late',
  ] as const
  export type RequestCategory = (typeof REQUEST_CATEGORIES)[number]

  // Read off the request's ref; 'hashed' when the ref was too long to store as it was.
  export type RequestOrigin =
    | { kind: 'workflow'; workflowId: number; leadUuid: string; noFilter: boolean }
    | { kind: 'manual_send'; workflowId: number; leadUuid: string }
    | { kind: 'export'; exportId: number; chunk: number }
    | { kind: 'reveal'; userId: number | null }
    | { kind: 'outreach_connection'; campaignId: string }
    | { kind: 'auto_engage'; source: AutoEngageSource }
    | { kind: 'hashed' }
    | { kind: 'other' }

  export type ProviderJobSummary = {
    id: number
    provider: string
    kind: string
    status: string
    providerStatus: string | null
    peopleFound: number | null
    cost: number | null
    startedAt: string
    finishedAt: string | null
    lastError: string | null
  }

  export type CreditRequest = {
    id: number
    createdAt: string
    meter: CreditMeter
    state: ReservationState
    reason: ReservationReason | null
    late: boolean
    requested: number
    granted: number
    returned: number | null
    delivered: number | null
    // Confirmed only: people this request unlocked, and those delivered but already charged this month.
    charged: number | null
    free: number | null
    ref: string
    // The export, workflow lead, send, reveal or campaign slot it belongs to; null on rows from before it was kept.
    requestKey: string | null
    domain: string | null
    origin: RequestOrigin
    // A refusal is decided on used + reserved against the limit.
    usageAtDenial: CreditUsage | null
    traceId: string | null
    confirmedAt: string | null
    expiresAt: string
    job: ProviderJobSummary | null
  }

  export type MeterTotals = { charged: number; free: number }

  // One export, workflow lead (with its retry), send, reveal or Auto-Engage purchase.
  export type CreditRequestItem = {
    // The request key; `reservation:<id>` for a row from before keys were kept.
    key: string
    requestKey: string | null
    createdAt: string
    origin: RequestOrigin
    domains: string[]
    // Over the confirmed reservations; null when none of that meter delivered.
    totals: Record<CreditMeter, MeterTotals | null>
    // Oldest first.
    requests: CreditRequest[]
  }

  export type FirstCharge = {
    at: string
    // Null for an unlock of the old path, before the ledger.
    reservationId: number | null
    origin: RequestOrigin | null
  }

  export type DeliveredPerson = {
    personKey: string
    meter: CreditMeter
    reservationId: number
    domain: string | null
    charged: boolean
    // Free only: the unlock this month that charged them.
    firstCharge: FirstCharge | null
  }

  export type DeliveredPeople = { rows: DeliveredPerson[]; total: number }

  export type CreditUnlock = {
    personKey: string
    name: string | null
    meter: CreditMeter
    source: string
    provider: string
    createdAt: string
    reservationId: number | null
    origin: RequestOrigin | null
  }

  export type ActivityPage<T> = { rows: T[]; total: number; page: number; pageSize: number }

  // The sweep expires holds every minute, so a hold a little past its expiry is still normal.
  export const STUCK_AFTER_EXPIRY_MS = 2 * 60_000

  export type Backlog = { count: number; oldestAt: string | null }

  // The month at a glance in Bunia. switchAt: the switch instant (CREDIT_LEDGER_SINCE), null when unset.
  export type CreditSummary = Credits & {
    switchAt: string | null
    ledger: Record<CreditMeter, Omit<CreditUsage, 'limit'>>
    // Limit 0 on a provider that refuses at the limit: every request for that meter is refused from the switch.
    refusesAll: Record<CreditMeter, boolean>
    requests: Record<'all' | RequestCategory, number>
    waiting: Backlog
    // Held past expiry + STUCK_AFTER_EXPIRY_MS when read; the read then expires them.
    stuck: Backlog
    slowJobs: Backlog
  }
}

export namespace WaterfallIoContacts {
  type TitleFilter = {
    name: string
    filter: string
  }

  export type ProspectorLauncherFilters = {
    domain: string
    webhook_url?: string
    custom_fields?: Record<string, string>
    limit?: number
    company_name?: string
    linkedin?: string
    verified_only?: boolean
    location_country?: string
    location_name?: string
    excluded_names?: string[]
    location_countries?: string[]
    include_phones?: boolean
    title_filters?: TitleFilter[]
    title_filter?: string
  }

  export type ProspectorLauncherResponse = {
    job_id: string
    start_date: string
  }

  type Task = {
    domain: string
    company_name: string
    webhook_url: string
    limit: number
    custom_fields: Record<string, string>
    job_id: string
    context_id: string
  }

  type Input = {
    task: Task
  }

  type Company = {
    id: string
    domain: string
    company_name: string
    website: string
    linkedin_id: string
    linkedin_url: string
    linkedin_description: string
    linkedin_logo_url: string | null
    size: string | null
    linkedin_size: string | null
    linkedin_industry: string | null
    linkedin_type: string | null
    linkedin_followers: number | null
    linkedin_founded: number | null
    linkedin_employees_count: number
    linkedin_address: string | null
    country: string | null
    generic_emails: string[]
  }

  export type EmailConfidence = 'high' | 'low'

  export type EmailStatus = 'invalid' | 'risky' | 'safe' | 'unknown' | 'provider_error'

  export type Seniority =
    | 'Owner'
    | 'CXO'
    | 'Partner'
    | 'Vice President'
    | 'Director'
    | 'Manager'
    | 'Senior'
    | 'Entry'
    | 'Other'

  export type Department =
    | 'Leadership'
    | 'Engineering'
    | 'Product Management'
    | 'Sales'
    | 'Marketing'
    | 'Customer Success'
    | 'Finance'
    | 'Accounting'
    | 'Legal'
    | 'Human Resources'
    | 'Information Technology'
    | 'Operations'
    | 'Education'
    | 'Other'

  export type Person = {
    id: string
    first_name: string | null
    last_name: string
    linkedin_id: string
    linkedin_url: string
    personal_email: string | null
    location: string | null
    country: string | null
    company_id: string
    company_linkedin_id: string
    company_name: string
    company_domain: string
    professional_email: string
    mobile_phone: string | null
    phone_numbers: string[] | null
    title: string
    seniority: Seniority
    department: Department
    email_verified: boolean
    email_confidence: EmailConfidence
    email_verified_status: EmailStatus
  }

  export type Usage = {
    total_usd: number
    balance_remaining_usd: number
    persons_count: number
    persons_usd: number
    phones_count: number
    phones_usd: number
    companies_count: number
    companies_usd: number
  }

  export type JobStatus = 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'TIMED_OUT' | 'ABORTED'

  export type ProspectorFinderRequest = {
    job_id: string
  }

  export type ProspectorFinderResponse = {
    status: JobStatus
    start_date: string
    stop_date?: string
    input: Input
    output: {
      company: Company
      persons: Person[]
      usage?: Usage
    }
  }

  export type SearchFilters = {
    domain: string
    company_linkedin?: string
    page_size?: number
    page_number?: number
    title_filters: TitleFilter[]
    custom_fields?: Record<string, string>
    location_countries?: string[]
  }
}
